// 外包 IP 工作台详情弹框内容组件（Offer 谈判详情）。
// 对齐截图结构：TabsAnchor 两个锚点区块 ——「产品信息」（只读）+「出价方案」（出价字段 + 谈判反馈）。
// 具体表单字段用 IPCooperationDemandFormModel 渲染（@mpc/common 共享表单模型，只读 descriptions 风格）；
// 核心商务条款 + IP合作游戏方详情经 FormModel 谈判表格字段（core_term_table / solution_table）加载，
// 谈判环境（editState/editable/onEditChange/onBatchAction）经 renderData.negotiation 注入；
// 权益明细复用内包 BenefitConfigNew 组件渲染（options 从 l2_info.benefits.template 解析，
//   外包 filter_options 不提供 ip_cooperation_benefit 权益定义，故不依赖该接口）；
// 谈判表格回复列 = 三个 Tag 按钮（接受绿/修改橙/拒绝红）+ 意见反馈 TextAreaPopover，数据收集走外层 editState；
// 历史轮次下拉 / 批量编辑在本组件承载。

import React, { useEffect, useMemo, useState } from 'react';
import { Divider, Select, Space, Tag } from '@bedrock/components';
import { ProCard } from '@bedrock/eed-react';
import IPCooperationDemandFormModel from '@mpc/common/src/features/IPCooperationDemand/IPCooperationDemandFormModel';
import { useDemandOptions } from '@mpc/common/src/features/useDemandOptions';
import { useIpResourceOptions } from '@mpc/common/src/features/IPCooperationDemand/useIpResourceOptions';
import BenefitConfigNew from '@mpc/common/src/features/IPCooperationDemand/BenefitConfig';
import {
  buildRowsFromBenefits,
  parseBenefitTemplateSchema,
  type BenefitGroupDef,
} from '@mpc/common/src/features/IPCooperationDemand/BenefitConfig/config';
import {
  buildPaymentPlanTableRows,
  buildSolutionTableRows,
  PAYMENT_PLAN_ITEM_KEY,
  resolveDisplayRound,
} from '@mpc/common/src/features/IPCooperationDemand/utils';
import type { FormItemRowProp } from '@mpc/common/src/components/SForm/createFormItemRow';
import { enums } from 'services/api';
import type { IpCooperationCopyrightOwnerDetailVo } from 'services/api';
import BudgetOfferTable from '@mpc/common/src/features/IPCooperationDemand/BudgetOfferTable';
import MusicBenefitSongsList from '@mpc/common/src/features/IPCooperationDemand/MusicBenefitTable/SongsList';
import AuthorizationInfoTable from '@mpc/common/src/features/IPCooperationDemand/NegotiationTable/AuthorizationInfoTable';

// 产品信息区块字段（外包走查调整，仅外包端生效）：
// - 合作IP 独占一行（col 24），名称过长自动换行
// - 合作产品信息 label 覆盖为「所属产品」（不动共享 FormModel）
// - 「联动所属区域」已移除
const PRODUCT_COLS = [
  { formItemKey: 'cooperation_ip', col: 24 },
  'business_user_ids',
  { formItemKey: 'cooperation_product', label: '所属产品' },
];

// 其他模块字段（保险 + 附件，FormModel 渲染，key 对齐详情 l2_info）
const OFFER_EXTRA_COLS = ['need_insurance', 'negotiation_attachments'];

// 静态区块 modelConf 类型（createFormItemRow 已注入 formItemMap）
type StaticModelConf = Omit<FormItemRowProp, 'formItemMap'>;

// 产品信息区块（静态，modelConf 稳定时 React.memo 跳过重渲染，隔离谈判编辑状态 setEditState 的全树重绘）
const ProductInfoSection = React.memo(({ modelConf }: { modelConf: StaticModelConf }) => (
  <IPCooperationDemandFormModel {...modelConf} itemCols={PRODUCT_COLS} />
));
// 其他区块（保险 + 附件，同理静态隔离）
const OfferExtraSection = React.memo(({ modelConf }: { modelConf: StaticModelConf }) => (
  <IPCooperationDemandFormModel {...modelConf} itemCols={OFFER_EXTRA_COLS} />
));

// 只读渲染配置（对齐内包 Drawer/utils DEFAULT_MODEL_CONF）
const MODEL_CONF = {
  renderType: 'descriptions' as const,
  row: { gutter: [24, 0] as [number, number] },
  globalItemProps: { view: true },
  descriptionsProps: {
    labelStyle: { backgroundColor: '#F8F9FA' },
  },
};

// 谈判条款编辑状态（共享自 NegotiationTable，供外层 DetailDrawer 传参）
export type { NegotiationEditState } from '@mpc/common/src/features/IPCooperationDemand/NegotiationTable';

interface DetailContentProps {
  /** 谈判详情 VO（由外层加载后传入） */
  detail?: IpCooperationCopyrightOwnerDetailVo | null;
  /** 是否可编辑（当前轮次 + 版权方待确认） */
  editable?: boolean;
  /** 逐条编辑状态（key=item_key） */
  editState?: Record<string, NegotiationEditState>;
  /** 编辑状态变更回调 */
  onEditChange?: (itemKey: string, patch: Partial<NegotiationEditState>) => void;
  /** 当前选中的谈判轮次 */
  round?: number;
  /** 可选轮次列表（1..current_round，前端构造占位，后端轮次接口就绪后替换） */
  rounds?: number[];
  /** 切换轮次（历史轮次只读） */
  onRoundChange?: (round: number) => void;
  /** 批量操作（Popover 选操作后整批应用；itemKeys 限定为当前表格实例的条款，缺省时全部） */
  onBatchAction?: (action: string, itemKeys?: string[]) => void;
  /** 外层 Form 实例（详情扁平值回填，镜像内包 SecondaryDetailContent setFieldsValue 范式） */
  form?: any;
}

const DetailContent: React.FC<DetailContentProps> = ({
  detail,
  editable = false,
  editState = {},
  onEditChange,
  round,
  rounds,
  onRoundChange,
  onBatchAction,
  form,
}) => {
  const l2 = detail?.l2_info;
  const [isInited, setIsInited] = useState(false);

  // 产品/商务人员选项（FormModel select/ProductInfoTable 展示依赖；$sType=outer 走 outsourceCommonFilterOptions）
  const { optionsMap } = useDemandOptions({
    keys: ['product', 'ip_cooperation_business_users'],
    cache: true,
  });
  // 联动所属区域/授权区域中文翻译选项（Apollo 动态下发，经外包端专属接口 /api/outsource/v2/ip-resource/options 加载，
  // 与内包端 options 同源组装；外包端 optionMap 注入后 FormModel linked_region / CoreTermTable 授权地区 code→中文名映射）
  const { countryRegionOptions, launchPlatformOptions } = useIpResourceOptions('external');

  // detail 嵌套 VO → 表单扁平值（只读渲染回填；镜像内包 flattenL2DetailForForm 的字段子集）
  const formData = useMemo(() => {
    const businessUsers = (l2?.business_users ?? []).map((u: any) => ({
      value: u?.id,
      user_id: u?.id,
      name: u?.name,
      user_name: u?.name,
    }));
    // 谈判「付款计划」条款（item_key=payment_plan）：回复/意见扁平注入付款计划行（对齐 solution_table 的 solution_detail 范式）
    const paymentNegotiation = (l2?.negotiation_items ?? []).find((i) => i.item_key === PAYMENT_PLAN_ITEM_KEY);
    // 产品信息兜底：l2 产品清单/IP 快照缺失时（刷新/时序导致 detail 未完整返回 l2 字段），用 l1 一级单产品/IP 快照兜底，
    // 保证「合作IP/合作产品信息/联动所属区域」只读展示不回退为 --（l2 有值时优先 l2，行为不变）
    const l1 = detail?.l1_info;
    const rawProducts = (l2?.products ?? []).length
      ? l2?.products ?? []
      : l1?.product_id
      ? [
          {
            product_id: l1.product_id,
            product_snapshot: l1?.product_snapshot,
            linked_region: l1?.linked_region,
            linked_platform: l1?.linked_platform,
          },
        ]
      : [];
    return {
      depth: 1,
      ip_resource_snapshot: l2?.ip_resource_snapshot ?? l1?.ip_resource_snapshot,
      products: rawProducts.map((p: any) => ({
        id: p?.product_id,
        name: p?.product_snapshot?.name,
        identifier: p?.product_snapshot?.identifier,
        linked_release_region: p?.linked_region,
        // 联动上架平台（镜像 linked_release_region；后端契约未接入，p 为 any 直接读取）
        linked_platform: p?.linked_platform,
        release_regions: p?.product_snapshot?.release_regions,
        launch_platforms: p?.product_snapshot?.launch_platforms,
        data_interconnection: p?.product_snapshot?.data_interconnection ? 1 : 0,
        product_managers: p?.product_snapshot?.product_managers,
      })),
      // IP合作游戏方详情：谈判表格受控数据（l2_info.auth_info.online_links → 行；复用 mpc-common
      // buildSolutionTableRows，对齐内包 flattenL2DetailForForm 构造；外包详情回显 SolutionTable 行数据）
      solution_table: buildSolutionTableRows(l2),
      // 付款计划（l2_info.payment_plan 嵌套 PaymentPlanItem[] → 扁平行；复用 mpc-common 共享构造器，
      // 付款计划表格（PaymentPlanTable）mode=external 数据列只读 + 回复/意见列可编辑）
      payment_plan: buildPaymentPlanTableRows(l2?.payment_plan, paymentNegotiation),
      business_user_ids: businessUsers,
      copyright_owner_name: l2?.copyright_owner_name,
      ip_level: l2?.ip_level,
      estimated_amount: l2?.estimated_amount,
      currency: l2?.currency,
      license_fee_form: l2?.license_fee_form,
      payment_terms: l2?.payment_terms,
      license_regions: l2?.license_regions,
      // 保险/附件（其他模块）：key 对齐详情 l2_info（need_insurance 0/1、negotiation_attachments）
      need_insurance: l2?.need_insurance,
      negotiation_attachments: l2?.negotiation_attachments,
      // 费用预算（字段 key 对齐接口 IpCooperationL2DesignForm，供 BudgetOfferTable 单元格 view 渲染）
      enable_license_fee: l2?.enable_license_fee,
      license_fee_amount: l2?.license_fee_amount,
      enable_min_guarantee: l2?.enable_min_guarantee,
      min_guarantee_amount: l2?.min_guarantee_amount,
      enable_royalty: l2?.enable_royalty,
      royalty_type: l2?.royalty_type,
      royalty_rate: l2?.royalty_rate,
      // 音乐权益（接口模型 IpCooperationMusicBenefitDto[]）：附加IP类型（extra_ip_type）+ music_benefit 数组（Form.List name="music_benefit" 绑定）
      extra_ip_type: (l2 as any)?.extra_ip_type,
      music_benefit: (l2 as any)?.music_benefit,
    };
    // 依赖含 detail.l1_info：l2 产品/IP 快照缺失时的 l1 兜底数据源变化（刷新/重新加载）必须触发 formData 重算，
    // 否则 l1 兜底停留在旧值（接口加载时序导致产品信息不回填）
  }, [l2, detail?.l1_info]);

  // 详情扁平值回填 Form（FormModel 字段 view 渲染依赖 form 值；镜像内包 SecondaryDetailContent benefit_rows 回填范式）
  useEffect(() => {
    if (!form) return;
    form.setFieldsValue?.({ ...formData, depth: 1 });
    setIsInited(true);
  }, [formData, form]);

  // 静态渲染上下文（产品信息/其他模块）：formData/detailData/optionMap 用 useMemo 稳定，
  // 配合 React.memo 区块避免谈判编辑状态（setEditState）变更时全树重渲染
  const staticRenderData = useMemo(
    () => ({
      formData,
      // detailData 顶层并入扁平字段（版权方/预估金额等），使 FormModel render 谓词能读到 l2_info 值
      detailData: { ...(detail ?? {}), ...formData },
      optionMap: {
        product: optionsMap?.['product'],
        ip_cooperation_business_users: optionsMap?.['ip_cooperation_business_users'],
        // 联动所属区域/授权区域中文翻译（外包端专属 ip-resource/options，Apollo region.options；FormModel linked_region + CoreTermTable 授权地区消费）
        country_region_options: countryRegionOptions,
        // 音乐上架平台-产品 选项（音乐权益模块）：外包端 ip-resource/options 返回的上架平台枚举
        launch_platform_options: launchPlatformOptions,
      },
    }),
    [formData, detail, optionsMap, countryRegionOptions],
  );
  // 谈判渲染上下文（核心商务/游戏方/付款计划表格）：editState 变化时重建，触发表格重渲染（编辑状态驱动，必要）
  const negotiationRenderData = useMemo(
    () => ({
      ...staticRenderData,
      // 付款计划谈判表格字段：外包版权方恒展示回复/意见列（对齐直引 PaymentPlanTable 时 showNegotiationColumns 恒真）
      showNegotiationColumns: true,
      // 谈判环境：供 FormModel 谈判表格字段（core_term_table/solution_table/payment_plan）读取
      // （对齐内包 benefit_rows 经 renderData.benefitOptions 注入的鸭子类型范式）；
      // mode='external'：外包仅可编辑回复/意见列，授权时长/联动周期等只读
      negotiation: { mode: 'external', editState, editable, onEditChange, onBatchAction },
    }),
    [staticRenderData, editState, editable, onEditChange, onBatchAction],
  );
  // 产品信息：恒只读（staticRenderData 稳定 → modelConf 稳定 → ProductInfoSection memo 生效）
  const productModelConf = useMemo(() => ({ ...MODEL_CONF, renderData: staticRenderData }), [staticRenderData]);
  // 其他（保险 + 附件）：editable 时才可编辑字段（FormModel 渲染）
  const offerExtraModelConf = useMemo(
    () => ({ ...MODEL_CONF, globalItemProps: { view: !editable }, renderData: staticRenderData }),
    [staticRenderData, editable],
  );
  // 费用预算（核心商务条款区块）：外包报价只读（MODEL_CONF view=true）+ 费用预算字段回填（staticRenderData.formData）
  const budgetModelConf = useMemo(() => ({ ...MODEL_CONF, renderData: staticRenderData }), [staticRenderData]);

  // 权益定义：从 l2_info.benefits.template 解析（外包 filter_options 不提供 ip_cooperation_benefit；
  // 干净数据 template 含 name/attributes，脏数据无 template 时 name 兜底 code）
  // 复用 config 层 parseBenefitTemplateSchema（与内包非方案设计态回填 schema 同源，避免两处解析漂移）
  const benefitOptions = useMemo<BenefitGroupDef[]>(() => {
    return (detail?.l2_info?.benefits || []).map((b) => {
      const tpl = parseBenefitTemplateSchema(b.template) ?? {};
      return {
        code: b.benefit_code,
        name: tpl.name || b.benefit_code || '权益',
        group: tpl.group,
        version: tpl.version,
        required_mode: tpl.required_mode,
        attributes: tpl.attributes,
      } as BenefitGroupDef;
    });
  }, [detail?.l2_info?.benefits]);

  // 权益明细行：benefits + options → buildRowsFromBenefits（config 层纯函数，attr 值回填 + required 默认开启）；
  // stateCode 传二级单状态：外包 options 从 detail template 解析（version 同源恒匹配），不触发版本过期分流，行为不变
  const benefitRows = useMemo(
    () => buildRowsFromBenefits(detail?.l2_info?.benefits, benefitOptions, detail?.state?.code),
    [detail?.l2_info?.benefits, benefitOptions, detail?.state?.code],
  );

  // 授权费形式中文名（出价方案头部只读字段，对齐设计稿「授权费形式：一次性买断」）
  const licenseFeeName = useMemo(() => {
    const v = l2?.license_fee_form;
    if (!v) return '';
    return enums?.IpLicenseFeeFormEnum?.values().find((o) => o.value === v)?.name || v;
  }, [l2?.license_fee_form]);

  // 当前轮次（l2_info.current_round）；编辑场景（版权方待确认可操作）当前待填轮 = current_round + 1，只读 = current_round；
  // 选中的历史轮次优先（round 非空）
  const currentRound = l2?.current_round ?? 0;
  const displayRound = resolveDisplayRound(editable, currentRound);
  const activeRound = round ?? displayRound;
  // 历史轮次下拉选项：已完成轮 [1..current_round] + 当前待填轮（编辑场景 current_round+1 不在已完成轮中，追加为当前项）
  const roundOptions = [
    ...(rounds || []).map((r) => ({ name: `第${r}轮`, value: r })),
    ...(displayRound > currentRound && currentRound > 0 ? [{ name: `第${displayRound}轮`, value: displayRound }] : []),
  ];

  return (
    <Space direction="vertical" size={16} className="w-full p-4">
      {/* 锚点1：产品信息（只读，React.memo 静态区块不随谈判编辑状态重渲染） */}
      <div data-is-anchor>
        <ProCard title="产品信息" expandable defaultExpanded>
          {/* key=l2.id：detail 重新加载（刷新/换单）时强制重建产品信息区块，避免 React.memo 基于旧 modelConf 残留上一次的产品信息 */}
          <ProductInfoSection key={l2?.id ?? 'empty'} modelConf={productModelConf} />
        </ProCard>
      </div>

      {/* 锚点2：出价方案（出价字段 + 谈判反馈 + 保险 + 附件） */}
      <div data-is-anchor>
        <ProCard
          // 走查：轮次 tag 移入头部「当前轮次」行，标题不再挂轮次
          title="出价方案"
          expandable
          defaultExpanded>
          {/* 头部：授权费形式（只读）+ 当前轮次 + 查看历史轮次下拉
              rec966Om：去掉标题旁轮次 tag，当前轮次 tag 移至本行（Size XS / Blue / 无 icon / 无 close），
              历史轮次→查看历史轮次，去掉「仅可查看」 */}
          {(licenseFeeName || rounds?.length || round != null) && (
            <div className="mb-3 flex items-center gap-3">
              {licenseFeeName ? (
                <span className="text-[14px]">
                  <span className="text-color-3">授权费形式：</span>
                  <span className="font-medium text-color-1">{licenseFeeName}</span>
                </span>
              ) : null}
              {displayRound > 0 && (
                <>
                  <Divider vertical />
                  <span className="flex items-center gap-1 text-[14px]">
                    <span className="text-color-3">当前轮次：</span>
                    <Tag size="xSmall" text={`第${displayRound}轮`} color="blue" />
                  </span>
                </>
              )}
              {rounds?.length ? (
                <>
                  <Divider vertical />
                  <span className="flex items-center gap-2 text-[14px]">
                    <span className="text-color-3">查看历史轮次：</span>
                    <Select
                      // recO8Oy：select 宽度自适应内容（去掉固定 w-[140px]），内部元素间距 8px；
                      // 最小宽 130 保证「当前」tag + 箭头不被遮挡（Design Feedback：加宽 20）
                      className="!w-auto !min-w-[130px]"
                      size="small"
                      placeholder="选择轮次"
                      value={round ?? activeRound}
                      onChange={(v: any) => onRoundChange?.(Number(v))}
                      options={roundOptions}
                      // 选中项：当前轮追加「当前」Tag（color light-brand / size XSmall，对齐设计稿）；间距 8px
                      renderSelectedItem={(option: any) => (
                        <span className="inline-flex items-center gap-2">
                          第{option.value}轮
                          {option.value === displayRound && <Tag size="xSmall" color="light-brand" text="当前" />}
                        </span>
                      )}
                    />
                  </span>
                </>
              ) : null}
            </div>
          )}

          {/* 子模块统一 ProCard level=2（简化 DOM，替代手动 div 小标题） */}
          <div className="flex flex-col">
            {/* 核心商务条款：费用预算表格（对齐内包端替换 CoreTermOfferTable；外包版权方报价只读 + 回复/意见可编辑） */}
            <ProCard level={2} title="核心商务条款" noPadding>
              <BudgetOfferTable
                items={l2?.negotiation_items}
                modelConf={budgetModelConf}
                mode="external"
                editable={editable}
                editState={editState}
                onEditChange={onEditChange}
                onBatchAction={onBatchAction}
              />
            </ProCard>

            {/* 付款计划：复用 mpc-common FormModel payment_plan 字段（同 IP合作授权信息范式，经 negotiationRenderData 注入谈判环境；
                外包版权方数据列只读 + 回复/意见可编辑，对齐设计稿 38368:314799「付款计划」卡片；
                数据来自 l2_info.payment_plan → buildPaymentPlanTableRows 扁平行，form.setFieldsValue 回填受控值）
                 isInited 避免首次加载时 formData 未初始化,内部protableplusform tree value不受控，
                 */}

            {isInited && (
              <ProCard level={2} title="付款计划" noPadding>
                <IPCooperationDemandFormModel
                  {...MODEL_CONF}
                  renderType={'row'}
                  renderData={negotiationRenderData}
                  itemCols={[{ formItemKey: 'payment_plan', noStyle: true }]}
                />
              </ProCard>
            )}

            {/* IP合作授权信息：授权区域(license_regions) + 授权时长(license_duration) 各独立条款回复表（AuthorizationInfoTable，行=字段）；
                外包版权方数据只读 + 回复/意见可编辑；行数据从 l2 派生（对齐 CoreTermTable 条款行模型） */}
            <ProCard level={2} title="IP合作授权信息" noPadding>
              <AuthorizationInfoTable
                items={l2?.negotiation_items}
                l2={l2}
                editState={editState}
                editable={editable}
                onEditChange={onEditChange}
                onBatchAction={onBatchAction}
                countryRegionOptions={countryRegionOptions}
                mode="external"
              />
            </ProCard>

            {/* 上线联动明细：单层平铺（序号/联动上线日期/联动下线日期/联动上线时长 + 回复/意见），整表一条 solution_detail 条款回复；经 FormModel solution_table 字段加载 */}
            <ProCard level={2} title="上线联动明细" noPadding>
              <IPCooperationDemandFormModel
                {...MODEL_CONF}
                renderType={'row'}
                renderData={negotiationRenderData}
                itemCols={[{ formItemKey: 'solution_table', noStyle: true }]}
              />
            </ProCard>

            {/* 权益明细：复用内包 BenefitConfigNew（options 从 benefits.template 解析）+ 谈判列（回复/意见反馈，权益说明只读） */}
            {/* 权益明细：非首个拆出的二级单（l2_info.first=false）隐藏，内外包一致由 first 字段控制 */}
            {(l2?.benefits?.length ?? 0) > 0 && (l2 as any)?.first !== false && (
              <ProCard level={2} title="权益明细" noPadding>
                <BenefitConfigNew
                  options={benefitOptions}
                  benefits={detail?.l2_info?.benefits}
                  value={benefitRows}
                  view
                  formData={staticRenderData?.formData}
                  negotiation={{
                    items: detail?.l2_info?.negotiation_items,
                    editState,
                    editable,
                    onEditChange,
                    onBatchAction,
                    // 外包谈判场景：回复/意见可编辑（BenefitConfigNew 仅 mode='external' 时放开编辑）
                    mode: 'external',
                  }}
                />
              </ProCard>
            )}


            {/* 音乐权益模块（附加IP类型=音乐 时展示；接口模型 IpCooperationMusicBenefitDto[]）：
                外层 MusicBenefitSongsList = 歌曲 Tab + Form.List name="music_benefit"，字段用 SFormItem 渲染，每首歌一个 action/opinion；
                外包版权方场景 mode=external：字段只读 + 回复/意见可编辑（ActionCell/OpinionCell） */}
            {(l2 as any)?.extra_ip_type === 'MUSIC' && (
              <ProCard level={2} title="音乐权益" noPadding >
                <IPCooperationDemandFormModel
                  {...MODEL_CONF}
                  renderData={staticRenderData}
                  itemCols={['music_benefit_count']}
                />
                <MusicBenefitSongsList
                  mode="external"
                  view
                  editable={editable}
                  editState={editState}
                  onEditChange={onEditChange}
                  onBatchAction={onBatchAction}
                  items={l2?.negotiation_items}
                  renderData={staticRenderData}
                />
              </ProCard>
            )}

            {/* 其他：保险 + 附件（React.memo 静态区块，editable 时才可编辑字段） */}
            <ProCard level={2} title="其他" noPadding>
              <OfferExtraSection modelConf={offerExtraModelConf} />
            </ProCard>
          </div>
        </ProCard>
      </div>
    </Space>
  );
};

export default DetailContent;
